/**
 * home.tsx — the studio entrance. Editorial hero, live provider status, the six
 * method packs as session launchers (the user gives a plain-language *scope*, never
 * a "construct target"), a workspace tally, and the governed pipeline.
 */
import { useState } from "react";
import { useRegistry, useWorkspace } from "../lib/hooks.ts";
import { useAction } from "../lib/actions.ts";
import { useT } from "../lib/i18n.tsx";
import { navigate, Link } from "../lib/router.tsx";
import { Icon } from "../lib/format.tsx";

export function HomeRoute(): JSX.Element {
  const reg = useRegistry();
  const { ws } = useWorkspace();
  const t = useT();
  const [openPack, setOpenPack] = useState<string | null>(null);

  const counts = ws
    ? {
        sessions: Object.keys(ws.session).length,
        evidence: Object.keys(ws.evidence).length,
        interpretations: Object.keys(ws.interpretation).length,
        assets: Object.keys(ws.asset).length,
        approved: Object.values(ws.asset).filter((a) => a.status === "approved").length,
        projections: Object.keys(ws.projection).length,
      }
    : null;

  const hero = t("home.hero").split("{em}");
  const lede = t("home.lede").split("{b}");

  return (
    <div className="wrap">
      <header style={{ padding: "44px 0 30px" }}>
        <div className="sec-no" style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <span style={{ width: 34, height: 1, background: "var(--phosphor-deep)", display: "inline-block" }} />
          {t("home.kicker")}
        </div>
        <h1 className="page-title" style={{ fontSize: "clamp(40px,7vw,82px)", lineHeight: 0.98, maxWidth: "16ch", marginTop: 22 }}>
          {hero[0]}
          <em>{t("home.hero.em")}</em>
          {hero[1]}
        </h1>
        <p className="page-desc" style={{ fontSize: 18, marginTop: 26, maxWidth: "62ch" }}>
          {lede[0]}
          <b style={{ color: "var(--paper)" }}>{t("home.lede.b")}</b>
          {lede[1]}
        </p>
        {reg ? (
          <div className="row" style={{ marginTop: 22 }}>
            <span className={`pill ${reg.provider.synthetic ? "warn" : "ok"}`}>AI: {reg.provider.id} · {reg.provider.model}</span>
            <span className="mini">{reg.provider.synthetic ? t("home.provider.synthetic") : t("home.provider.live")}</span>
          </div>
        ) : null}
      </header>

      {counts ? (
        <div className="row" style={{ gap: 0, borderTop: "1px solid var(--line)", borderBottom: "1px solid var(--line)", margin: "10px 0 36px" }}>
          {(["sessions", "evidence", "interpretations", "assets", "approved", "projections"] as const).map((k) => (
            <div key={k} style={{ padding: "16px 26px 16px 0", marginRight: 26, borderRight: "1px solid var(--line-soft)" }}>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 32, color: "var(--phosphor)", lineHeight: 1 }}>{counts[k]}</div>
              <div className="foot-k" style={{ marginTop: 6 }}>{t(`count.${k}`)}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="page-head">
        <div>
          <div className="sec-no">{t("home.elicit.no")}</div>
          <h2 className="page-title" style={{ fontSize: "clamp(24px,3.5vw,38px)" }}>{t("home.elicit.title")}</h2>
        </div>
        <p className="page-desc">{t("home.elicit.desc")}</p>
      </div>

      <div className="packs">
        {reg?.packs.map((p) => (
          <div className="pack" key={p.slug} onClick={() => setOpenPack(openPack === p.slug ? null : p.slug)}>
            <div className="pid">{p.methodPackId}</div>
            <div className="pn">{p.name}</div>
            <div className="pp">{p.purpose}</div>
            <div className="pcount">▸ {t("pack.primitives", { n: p.primitiveSlugs.length, status: p.status })}</div>
          </div>
        ))}
      </div>

      {openPack ? <StartSession packSlug={openPack} onCancel={() => setOpenPack(null)} /> : null}

      <div className="page-head" style={{ marginTop: 56 }}>
        <div>
          <div className="sec-no">{t("home.pipeline.no")}</div>
          <h2 className="page-title" style={{ fontSize: "clamp(24px,3.5vw,38px)" }}>{t("home.pipeline.title")}</h2>
        </div>
        <Link to="/registry" className="btn ghost">
          <Icon name="stack" /> {t("home.registry.link")}
        </Link>
      </div>
      <GovernanceChain stages={reg?.pipeline ?? []} />
    </div>
  );
}

function StartSession({ packSlug, onCancel }: { packSlug: string; onCancel: () => void }): JSX.Element {
  const [title, setTitle] = useState("");
  const [scope, setScope] = useState("");
  const { run, busy } = useAction();
  const t = useT();
  return (
    <div className="specimen" style={{ marginTop: 18 }}>
      <div className="spec-tab">
        <span className="spec-id"><b>session.start</b> · {packSlug}</span>
        <span className="spec-fam">elicit</span>
      </div>
      <div className="spec-body">
        <div className="field">
          <div className="field-label"><span>{t("start.title.label")}</span></div>
          <input className="field-input" placeholder={t("start.title.ph")} value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div className="field">
          <div className="field-label"><span>{t("start.scope.label")}</span></div>
          <textarea className="field-input" rows={3} placeholder={t("start.scope.ph")} value={scope} onChange={(e) => setScope(e.target.value)} />
        </div>
        <div className="btn-row">
          <button
            className="btn primary"
            disabled={!title.trim() || !scope.trim() || busy}
            onClick={async () => {
              const ack = await run("session.start", { methodPack: packSlug, title: title.trim(), scope: scope.trim() });
              if (ack?.goto) navigate(ack.goto.slice(1));
            }}
          >
            {t("start.open")}
          </button>
          <button className="btn ghost" onClick={onCancel}>{t("common.cancel")}</button>
        </div>
      </div>
    </div>
  );
}

export function GovernanceChain({ stages }: { stages: readonly { n: number; step: string; name: string; entity: string; text: string; boundary: string }[] }): JSX.Element {
  return (
    <div className="gov-track">
      {stages.map((s) => (
        <div className="gov-stage" key={s.n}>
          <div className="gov-rail">
            <div className="gov-dot" />
            <div className="gov-line" />
          </div>
          <div className="gov-card">
            <div className="gov-step">{s.step}</div>
            <div className="gov-name">{s.name}</div>
            <div className="gov-entity">{s.entity}</div>
            <div className="gov-text">{s.text}</div>
            <div className="gov-boundary">
              <span className="x">≠</span>
              {s.boundary.replace("≠", "").trim()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
